import { Dayjs } from 'dayjs';

import { TimeZone } from '../../../time/timezone.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { VersionRange } from '../../core/version-range.js';
import { DataModel, DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { cloneList, isSameList } from '../../dto/data-model-utils.js';

export class NewsletterSentLog extends DataModel {
  static readonly TYPE_ID: string = '0840fe27aa8c46788a1e4315f92f8f36';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
      public readonly uniqueId: string,
      public readonly creatorId: string,
      public readonly creationDate: Dayjs,
      public readonly subject: string,
      public readonly numberOfEmailsSent: number,
      public readonly events: readonly string[],
  ) {
    super();
  }

  get $typeId() {
    return NewsletterSentLog.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.exactly(NewsletterSentLog.SCHEMA_VERSION);
  }

  static create(args: {
      uniqueId: string,
      creatorId: string,
      creationDate: Dayjs,
      subject: string,
      numberOfEmailsSent: number,
      events: readonly string[],
  }): NewsletterSentLog {
    return new NewsletterSentLog(
        args.uniqueId,
        args.creatorId,
        args.creationDate,
        args.subject,
        args.numberOfEmailsSent,
        args.events,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): NewsletterSentLog {
    return NewsletterSentLog.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      creatorId: MapUtils.getString(map, 'creatorId'),
      creationDate: context.decodeDate(MapUtils.getInteger(map, 'creationDate')),
      subject: MapUtils.getString(map, 'subject'),
      numberOfEmailsSent: MapUtils.getInteger(map, 'numberOfEmailsSent'),
      events: MapUtils.getList(map, 'events', JSONUtils.decodeString),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'creationDate': context.encodeDate(this.creationDate),
      'subject': this.subject,
      'numberOfEmailsSent': this.numberOfEmailsSent,
      'events': this.events,
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    creatorId = this.creatorId,
    creationDate = TimeZone.cloneDate(this.creationDate),
    subject = this.subject,
    numberOfEmailsSent = this.numberOfEmailsSent,
    events = cloneList(this.events),
  }: {
    uniqueId?: string,
    creatorId?: string,
    creationDate?: Dayjs,
    subject?: string,
    numberOfEmailsSent?: number,
    events?: readonly string[],
  } = {}): this {
    return NewsletterSentLog.create({
      uniqueId,
      creatorId,
      creationDate,
      subject,
      numberOfEmailsSent,
      events,
    }) as this;
  }

  isSame(other: NewsletterSentLog): boolean {
    if(other === this) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
      other.creatorId === this.creatorId &&
      TimeZone.isSameDay(other.creationDate, this.creationDate) &&
      other.subject === this.subject &&
      other.numberOfEmailsSent === this.numberOfEmailsSent &&
      isSameList(other.events, this.events);
  }
}

export class NewsletterSentLogBuilder extends DataModelBuilder<NewsletterSentLog> {
  build(context: SerializationContext, map: SerializedData): NewsletterSentLog {
    return NewsletterSentLog.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(_context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(NewsletterSentLog.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(NewsletterSentLog.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    if(versionRange.to < 2) {
      map['events'] = [];
    }

    map['$schema_version'] = VersionRange.encodedExactly(NewsletterSentLog.SCHEMA_VERSION);
    return map;
  }
}
